import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  // Avatar,
  Badge,
  Box,
  Button,
  Chip,
  // Fab,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DataGridPro,
  gridClasses,
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
  Add,
  Delete,
  DeleteForever,
  DeleteForeverOutlined,
  DeleteOutlined,
  // DoneOutlined,
  FilterList,
  // JoinInner,
  RestoreFromTrash,
  // SyncAltOutlined,
  ViewColumn,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useClip } from "../../store/clip/hooks";
import { useAuth } from "../../store/auth/hooks";
import { Feature } from "flagged";
import { STIPA_STOI } from "../../FeatureFlags";
import { ClipTitleBarMenu } from "../../components/titleBarMenu/ClipTitleBarMenu";
import CircularProgress from "@mui/material/CircularProgress";
import { CaseTitleBarMenu } from "../../components/titleBarMenu/CaseTitleBarMenu";
import { useNavigate, useParams } from "react-router-dom";
import { ContentTitleBar } from "../../components/ContentTitleBar";
import { fullCaseReset } from "../../App";
import "../AppPage.css";
// import { CombineClipsDialog } from "../../components/dialogs";
import { WaitDialog } from "../../components/dialogs";
import {
  formatDateRelative,
  formaTimeFromDateString,
  formatDateFromDateString,
  formatHHMMSSFromMs,
} from "./utils/formatDate";
import { getNavigationUrl } from "../../store/navigationUtil";
import { loadCase, loadClips } from "./utils/dataLoader";
import { canEditCase } from "../../store/AclUtil";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { WEBSOCKET_URL } from "../../store/config";
import {
  animationDuration,
  animationKeyFramesGrow,
  animationKeyFramesPulse,
  animationKeyFramesShrink,
  animationTiming,
} from "./utils/animation";
import { clipStatus, CLIP_PROCESSING_STATUSES } from "./utils/clipStatus";
import {
  buildDriveUsageRequest,
  driveUsageBytes,
  CLIP_SCOPE,
  DRIVE_USAGE,
  DRIVE_USAGE_TIMEOUT_MS,
} from "./utils/driveUsage";
import { humanFileSize } from "../../store/formatUtil";
import { LoadingDots } from "../../components/LoadingDots";
import { useUploadQueue } from "../../store/uploadQueue/hooks";
import {
  SYNC_FILTERING_COMPLETE,
  UPLOADING,
  WAITING_FOR_FILES,
} from "../../store/statuses";
import { NEW_CLIP_COMMIT_TYPES } from "../../store/commitTypes";
import { useCase } from "../../store/case/hooks";
import { useSnackbars } from "../../store/snackbars/hooks";
// import { useModals } from "../../store/dialogs/hooks";
import { StipaAlert } from "../../components/alerts";
import { HistoryDrawer } from "../../components/HistoryDrawer";
import { StoiFailureSnackbar } from "../../components/snackbars";
// import { CombineClipsSnackbar } from "../../components/snackbars";

export default function ClipsPage() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { emptyTrash, isEmptying } = useCase();
  // const { openCombineClipsDialog } = useModals();
  const { caseId } = useParams();
  const { restoreClip } = useClip();
  const { openStoiFailedSnackbar } = useSnackbars();
  const apiRef = useGridApiRef();
  const { accessCode, user } = useAuth();
  const { clipId, isUploading, uploadFinishedAt } = useUploadQueue();
  const [af_case, setAf_case] = useState({});
  const [caseClips, setCaseClips] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: "include",
    ids: new Set(),
  });
  const [readOnly, setReadOnly] = useState(true);
  const [websocketRegistered, setWebsocketRegistered] = useState(false);
  // On-disk size per clip, keyed by clip id. Kept apart from caseClips because
  // a CLIP_STATUS message reloads that list wholesale, which would throw the
  // measurements away.
  const [driveUsage, setDriveUsage] = useState({});
  const [measuringDriveUsage, setMeasuringDriveUsage] = useState(false);
  // The correlation id of the batch we asked for. Drive usage messages fan out
  // to every session this user has open, so this is the only way to tell our
  // own batch's terminator from another tab's.
  const driveUsageCorrelationId = useRef(null);
  // Check if datagrid is sorted to prevent animation running on sort
  const [isSorted, setIsSorted] = useState(false);
  // ID of element selected for editing to be able to compare to next render
  // of datagrid
  const [prevId, setPrevId] = useState();
  // Index in rendered grid of element selected for editing
  const [prevIndex, setPrevIndex] = useState();
  // Check to not display grid once loaded until indices can be compared
  // to determine if index has changed
  const [comparingIndices, setComparingIndices] = useState(false);
  // Type of animation is determined by whether an element has changed
  // position and has a different index than the previous render
  const [isNewIndex, setIsNewIndex] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState();
  // Clip selected for deletion: enables shrink animation to run before updating list
  const [isExiting, setIsExiting] = useState({});
  // Most recently updated clip (using this approach in case the default sort should change
  // in future updates)
  const lastModifiedRow = caseClips.reduce(
    (acc, curr) => (curr.lastModified > acc.lastModified ? curr : acc),
    caseClips[0],
  );
  const [numberTrashed, setNumberTrashed] = useState(0);
  const [isTrashView, setIsTrashView] = useState(false);
  const [singleChannelClips, setSingleChannelClips] = useState([]);
  // Determines if new clip has been added based on commit type as opposed
  // to existing clip being edited
  const lastModifiedRowIsNewClip = NEW_CLIP_COMMIT_TYPES.includes(
    lastModifiedRow?.commitType,
  );

  // const [selectedAlignedClip, setSelectedAlignedClip] = useState(null);

  // For use in combine clips feature
  // const [alignedClips, setAlignedClips] = useState([]);

  // const displayAlignedClick = (row) =>
  //   Boolean(selectedAlignedClip) && selectedAlignedClip === row.id
  //     ? setSelectedAlignedClip(null)
  //     : setSelectedAlignedClip(row.id);

  // const alignedLabel = (row) => {
  //   const hasMatchingClips =
  //     row.alignedClipIds.length > 0 &&
  //     caseClips.some((clip) => row.alignedClipIds.includes(clip.id));

  //   if (!hasMatchingClips) {
  //     return "No Aligned Clips";
  //   }
  //   if (row.id === selectedAlignedClip && !isTrashView) {
  //     return "Clear Aligned Clips Selection";
  //   }
  //   return "Show Aligned Clips";
  // };

  // const alignedStyles = (row) => {
  //   if (row.id === selectedAlignedClip) {
  //     return { color: "info", box: 3 };
  //   }
  //   if (row.alignedClipIds.includes(selectedAlignedClip)) {
  //     return { color: "secondary", box: 2 };
  //   }
  //   return { color: "default", box: 1 };
  // };

  // const combineBtnDisabled = (row) => {
  //   if (row.id !== selectedAlignedClip || isTrashView) return false;
  //   const caseClipIds = new Set(caseClips.map((item) => item.id));
  //   return (
  //     row.alignedClipIds.length > 0 &&
  //     row.alignedClipIds.some((id) => caseClipIds.has(id))
  //   );
  // };

  const systemProcessingSpinner = (row) => {
    const isStatusProcessing = CLIP_PROCESSING_STATUSES.includes(row?.status);
    const isCurrentlyUploading =
      row?.status === UPLOADING && isUploading && row?.id === clipId;
    const isOtherProcessingStatus =
      isStatusProcessing && row?.status !== UPLOADING;

    if (isCurrentlyUploading || isOtherProcessingStatus) {
      return (
        <Box display="flex" ml={2}>
          <CircularProgress disableShrink size={20} />
        </Box>
      );
    }

    return null;
  };

  const CustomToolbar = () => {
    return (
      columnVisibilityModel && (
        <Toolbar>
          <Tooltip title="Filters">
            <FilterPanelTrigger
              sx={{
                color: isTrashView
                  ? theme.palette.secondary.dark
                  : theme.palette.primary.dark,
              }}
              render={(props, state) => (
                <ToolbarButton {...props} color="default">
                  <Badge badgeContent={state.filterCount} color="primary">
                    <FilterList fontSize="small" />
                  </Badge>
                </ToolbarButton>
              )}
            />
          </Tooltip>
          <Tooltip title="Columns">
            <ColumnsPanelTrigger
              render={<ToolbarButton />}
              sx={{
                color: isTrashView
                  ? theme.palette.secondary.dark
                  : theme.palette.primary.dark,
              }}
            >
              <ViewColumn fontSize="small" />
            </ColumnsPanelTrigger>
          </Tooltip>
        </Toolbar>
      )
    );
  };

  const columns = [
    {
      field: "description",
      headerName: "Clip",
      hideable: false,
      flex: 1.5,
      cellClassName: "columnHeaderLarge",
      renderCell: ({ row, value }) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          height={1}
          width={1}
        >
          <Typography noWrap>{value}</Typography>
          {systemProcessingSpinner(row)}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: ({ row, value }) => {
        if (row.deleted) {
          return <Chip color="secondary" label="Trashed" />;
        }
        if (user.email === row.comment && row.status === UPLOADING) {
          if (isUploading && row.id === clipId) {
            return (
              <Chip
                color={clipStatus[value].color}
                label={clipStatus[value].label}
              />
            );
          } else if (
            !isUploading ||
            (isUploading &&
              row.id !== clipId &&
              uploadFinishedAt &&
              Date.now() - uploadFinishedAt > 5000)
          ) {
            return <Chip color="warning" label="Import Interrupted" />;
          }
        }
        return (
          <Chip
            color={clipStatus[value].color}
            label={clipStatus[value].label}
          />
        );
      },
    },
    {
      field: "length",
      headerName: "Length",
      valueGetter: (value) => formatHHMMSSFromMs(value),
    },
    {
      field: "numberOfChannels",
      headerName: "Channels",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    // {
    //   field: "synced",
    //   headerName: "Common Clock",
    //   align: "center",
    //   headerAlign: "center",
    //   flex: 1,
    //   minWidth: 150,
    //   renderCell: ({ row }) => (
    //     <Box
    //       display="flex"
    //       alignItems="center"
    //       justifyContent="center"
    //       height={1}
    //       width={1}
    //     >
    //       <Tooltip title={row.synced ? "Common Clock" : "Not Common Clock"}>
    //         <Avatar
    //           aria-label={row.synced ? "Common Clock" : "Not Common Clock"}
    //           size="small"
    //           sx={{
    //             backgroundColor: row.synced
    //               ? "success.main"
    //               : theme.palette.action.disabledBackground,
    //             flexShrink: 0,
    //           }}
    //         >
    //           <DoneOutlined fontSize="small" />
    //         </Avatar>
    //       </Tooltip>
    //     </Box>
    //   ),
    // },
    // {
    //   field: "align_combine",
    //   headerName: "Align | Combine",
    //   flex: 1,
    //   minWidth: 160,
    //   filterable: false,
    //   sortable: false,
    //   renderCell: ({ row }) => (
    //     <Box display="flex" alignItems="center" gap={1} height={1} width={1}>
    //       <Tooltip title={alignedLabel(row)}>
    //         <span>
    //           <Fab
    //             aria-label={alignedLabel(row)}
    //             color={alignedStyles(row).color}
    //             disabled={
    //               !Boolean(row.alignedClipIds.length) ||
    //               !caseClips
    //                 .map((item) => item.id)
    //                 .some((item2) => row.alignedClipIds.includes(item2))
    //             }
    //             onClick={() => displayAlignedClick(row)}
    //             size="small"
    //             sx={{
    //               boxShadow: alignedStyles(row).box,
    //               flexShrink: 0,
    //               transition: "all 250ms cubic-bezier(0.37, 0, 0.63, 1)",
    //             }}
    //           >
    //             <SyncAltOutlined fontSize="small" />
    //           </Fab>
    //         </span>
    //       </Tooltip>
    //       <Tooltip title="Combine Clips">
    //         <span>
    //           <Fab
    //             aria-label="Combine Clips"
    //             color={combineBtnDisabled(row) ? "primary" : "default"}
    //             disabled={!combineBtnDisabled(row)}
    //             onClick={() => openCombineClipsDialog()}
    //             size="small"
    //             sx={{
    //               boxShadow: combineBtnDisabled(row) ? 3 : "none",
    //               flexShrink: 0,
    //               transition: "all 250ms cubic-bezier(0.37, 0, 0.63, 1)",
    //             }}
    //           >
    //             <JoinInner fontSize="small" />
    //           </Fab>
    //         </span>
    //       </Tooltip>
    //     </Box>
    //   ),
    // },
    {
      field: "location",
      headerName: "Location",
      flex: 1.5,
    },
    {
      field: "createdDate",
      headerName: "Date Created",
      flex: 1,
      valueGetter: (_value, row) => new Date(row.createdEpochMs),
      valueFormatter: (value) => formatDateFromDateString(value.getTime()),
      type: "date",
    },
    {
      field: "recordingDate",
      headerName: "Date Recorded",
      flex: 1,
      valueGetter: (_value, row) => new Date(row.recordingTime),
      valueFormatter: (value) => formatDateFromDateString(value.getTime()),
      type: "date",
    },
    {
      field: "recordingTime",
      headerName: "Time Recorded",
      flex: 1,
      valueGetter: (_value, row) => new Date(row.recordingTime),
      valueFormatter: (value) => formaTimeFromDateString(value.getTime()),
      type: "date",
    },
    {
      field: "createdBy",
      headerName: "Created by",
      flex: 1.5,
      valueGetter: (_value, row) => row.createdBy.name,
    },
    {
      field: "lastModified",
      headerName: "Modified",
      flex: 1.5,
      valueGetter: (value) => new Date(value),
      valueFormatter: (value) => formatDateRelative(value.getTime()),
      type: "date",
    },
    {
      field: "driveUsage",
      headerName: "Drive Usage",
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={1}
          width={1}
        >
          {value === undefined ? (
            // Still measuring, or the request never came back.
            measuringDriveUsage ? (
              <LoadingDots />
            ) : (
              <Typography variant="body2">&mdash;</Typography>
            )
          ) : (
            <Typography variant="body2">
              {value === null ? "—" : humanFileSize(value, true)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "Actions",
      hideable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 40,
      renderHeader: () => null,
      renderCell: ({ row }) => {
        return isTrashView ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={1}
            width={1}
          >
            <Tooltip title="Restore Clip">
              <IconButton
                aria-label="restore clip"
                color="secondary"
                onClick={() => handleRestore(row.id)}
              >
                <RestoreFromTrash />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={1}
            width={1}
          >
            <ClipTitleBarMenu
              singleChannelClips={singleChannelClips}
              user={user}
              clip={row}
              readOnly={readOnly}
              onChange={dataChanged}
              isClipsPage
              // selectedAlignedClip={selectedAlignedClip}
              // clearSelectedAlignedClip={() => setSelectedAlignedClip(null)}
            />
          </Box>
        );
      },
    },
  ];

  const gridAnimationStates = useCallback(() => {
    setRowSelectionModel({ type: "include", ids: new Set() });
    setIsNewIndex(
      apiRef.current?.state?.sorting?.sortedRows[prevIndex] !== prevId,
    );
    if (Boolean(caseClips.length)) {
      setColumnVisibilityModel({
        location: Boolean(
          caseClips.map((item) => item.location).filter(Boolean).length,
        ),
        recordingDate: Boolean(
          caseClips.map((item) => item.recordingTime).filter(Boolean).length,
        ),
        recordingTime: Boolean(
          caseClips.map((item) => item.recordingTime).filter(Boolean).length,
        ),
      });
    }
    if (
      Boolean(!prevId) &&
      Boolean(lastModifiedRow) &&
      !lastModifiedRowIsNewClip &&
      Math.floor((Date.now() - lastModifiedRow.lastModified) / 1000) < 5
    ) {
      setRowSelectionModel({
        type: "include",
        ids: new Set([lastModifiedRow.id]),
      });
    }
    setComparingIndices(false);
    setIsSorted(false);
    setIsExiting({});
  }, [
    caseClips,
    lastModifiedRow,
    lastModifiedRowIsNewClip,
    prevId,
    prevIndex,
    apiRef,
  ]);

  const gridAnimationClasses = (id) => {
    if (id === isExiting?.id) {
      return "shrinkRowAnimate";
    } else if (
      !isSorted &&
      id === lastModifiedRow?.id &&
      Math.floor((Date.now() - lastModifiedRow?.lastModified) / 1000) < 5
    ) {
      if ((Boolean(!prevId) && lastModifiedRowIsNewClip) || isNewIndex) {
        return "growRowAnimate";
      } else if (
        !lastModifiedRowIsNewClip &&
        !isNewIndex &&
        Boolean(![...rowSelectionModel.ids].length)
      ) {
        return "pulseRowAnimate";
      }
    }
  };

  /**
   * Drive usage arrives one message per clip, then a terminator carrying
   * `complete: true`. Ordering is guaranteed server-side, so the terminator
   * can't overtake the results it closes out.
   */
  const handleDriveUsageMessage = (payload) => {
    if (!payload) return;
    const { correlationId, usage, complete, error } = payload;

    // Take any clip measurement, even from a correlation id we never issued:
    // the server also pushes updates unprompted when something changes on
    // disk, which saves the client from having to know which clip statuses
    // affect footprint. Clip ids are unique across cases, so a message meant
    // for another tab's case can't land on one of our rows.

    // TODO: change scope to user or case
    if (usage && usage.scope === CLIP_SCOPE) {
      setDriveUsage((prev) => ({ ...prev, [usage.id]: usage }));
    }

    // Only our own batch drives the loading state.
    if (!correlationId || correlationId !== driveUsageCorrelationId.current) {
      return;
    }
    if (error) {
      console.error("[ClipsPage][WebSocket] Drive usage error:", error);
    }
    if (complete) {
      driveUsageCorrelationId.current = null;
      setMeasuringDriveUsage(false);
    }
  };

  const { sendMessage, readyState } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (event && event.data) {
        if (event.data === "pong") return;
        const data = JSON.parse(event.data);
        if (data.command === "CLIP_STATUS") {
          const { clipId, clipStatus } = data.data;
          if (clipStatus === "DELETE_PENDING") {
            setIsExiting(caseClips.find((item) => item.id === clipId));
            setTimeout(() => {
              loadClipsToState();
            }, 500);
          } else {
            loadClipsToState();
          }
        } else if (data.command === DRIVE_USAGE) {
          handleDriveUsageMessage(data.data);
        } else if (data.command === "STOI_FAILED") {
          const { clipId, reason } = data.data;
          const { description } = caseClips.find((item) => item.id === clipId);
          openStoiFailedSnackbar({ reason, description });
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      const payload = {
        command: "REGISTER",
        authorization: accessCode,
      };
      sendMessage(JSON.stringify(payload));
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  /**
   * Ask for drive usage once per case, after REGISTER has gone out -- only
   * registered sessions are in the server's cache, and answers to an
   * unregistered session go nowhere. This effect runs a render later than the
   * registration effect above, so the ordering holds.
   */
  useEffect(() => {
    if (!websocketRegistered || !caseId) return;

    const correlationId = crypto.randomUUID();
    driveUsageCorrelationId.current = correlationId;
    setDriveUsage({});
    setMeasuringDriveUsage(true);
    // NOTE: for user and case loop over each user/case and send a request for each user/case combination
    sendMessage(buildDriveUsageRequest(accessCode, caseId, correlationId));

    // A payload the server can't parse is dropped without a reply, so stop
    // waiting rather than animating forever.
    const timeout = setTimeout(() => {
      if (driveUsageCorrelationId.current === correlationId) {
        console.warn("[ClipsPage] Drive usage request timed out", caseId);
        driveUsageCorrelationId.current = null;
        setMeasuringDriveUsage(false);
      }
    }, DRIVE_USAGE_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [accessCode, caseId, websocketRegistered, sendMessage]);

  const dataChanged = () => {
    if (readyState !== ReadyState.OPEN) {
      load();
    }
  };

  const reloadCase = async () => {
    const currCase = await loadCase(caseId);
    if (!currCase) {
      navigate(getNavigationUrl());
    }
    setAf_case(currCase);
  };

  const caseDataChanged = () => reloadCase();

  const handleCellClick = (e) => {
    const { field, id, row } = e;
    const { alignedClipIds, status } = row;

    if (isTrashView || field === "Actions") return;
    if (field === "align_combine" && alignedClipIds.length > 0) return;
    if (status === SYNC_FILTERING_COMPLETE) {
      return navigate(`/app/cases/${caseId}/clips/${id}/syncResults`);
    }
    if (status === WAITING_FOR_FILES && isUploading) return;

    navigate(getNavigationUrl(null, row));
  };

  const loadClipsToState = useCallback(async () => {
    const currClips = await loadClips(caseId);
    if (!currClips) {
      // error with login
      navigate(getNavigationUrl());
      return;
    }
    isTrashView
      ? setCaseClips(currClips.filter((item) => item.deleted))
      : setCaseClips(currClips.filter((item) => !item.deleted));
    setNumberTrashed(currClips.filter((item) => item.deleted).length);
    // setSelectedAlignedClip(null);
  }, [caseId, isTrashView, navigate]);

  const load = useCallback(async () => {
    const currCase = await loadCase(caseId);
    if (!currCase) {
      navigate(getNavigationUrl());
    }
    setAf_case(currCase);
    await loadClipsToState();
    setComparingIndices(true);
    setLoaded(true);
    setReadOnly(!canEditCase(user, currCase));
  }, [caseId, navigate, user, loadClipsToState]);

  const applyTrashFilter = () => {
    // setSelectedAlignedClip(null);
    setIsTrashView(true);
  };

  const removeTrashFilter = () => {
    // setSelectedAlignedClip(null);
    setIsTrashView(false);
  };

  const toggleFilter = useCallback(async () => {
    setLoaded(false);
    setCaseClips([]);
    await load();
    setLoaded(true);
  }, [load]);

  useEffect(() => {
    toggleFilter();
  }, [isTrashView, toggleFilter]);

  const handleRestore = async (id) => {
    setLoaded(false);
    const selectedClip = caseClips.find((clip) => clip.id === id);
    await restoreClip(selectedClip);
    setLoaded(true);
    dataChanged();
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    gridAnimationStates();
    setSingleChannelClips(
      caseClips?.filter((item) => item.numberOfChannels === 1 && !item.deleted),
    );
  }, [caseClips]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleStateChange = (params) => {
      setPrevId([...rowSelectionModel.ids][0]);
      setPrevIndex(
        params.sorting.sortedRows.indexOf([...rowSelectionModel.ids][0]),
      );
    };

    return apiRef.current.subscribeEvent("stateChange", handleStateChange);
  }, [apiRef, rowSelectionModel]);

  // Measurements arrive separately from the clip list, so fold them in here
  // rather than into caseClips itself.
  const rows = useMemo(
    () =>
      caseClips.map((clip) => ({
        ...clip,
        driveUsage: driveUsageBytes(driveUsage[clip.id]),
      })),
    [caseClips, driveUsage],
  );

  // useEffect(() => {
  //   if (!selectedAlignedClip) {
  //     setAlignedClips([]);
  //     return;
  //   }
  //   const selectedClip = caseClips.find(
  //     (item) => item.id === selectedAlignedClip,
  //   );
  //   if (selectedClip) {
  //     const aligned = caseClips.filter((item) =>
  //       selectedClip.alignedClipIds.includes(item.id),
  //     );
  //     setAlignedClips([selectedClip, ...aligned]);
  //   }
  // }, [selectedAlignedClip, caseClips]);

  return (
    <Stack component="main" height={1}>
      <WaitDialog open={!loaded || comparingIndices} />
      <Stack flex={1} p={2}>
        <Stack height="calc(100vh - 222px)" maxHeight="calc(100vh - 222px)">
          <DataGridPro
            apiRef={apiRef}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            disableColumnMenu
            getCellClassName={({ field, row }) => {
              if (
                CLIP_PROCESSING_STATUSES.includes(row?.status) &&
                field !== "Actions" &&
                field !== "status"
              )
                if (row.status === UPLOADING) {
                  if (!isUploading || (isUploading && row.id !== clipId)) {
                    return;
                  } else {
                    return "processing";
                  }
                } else {
                  return "processing";
                }
            }}
            getRowClassName={({ id }) => gridAnimationClasses(id)}
            hideFooter
            hideFooterPagination
            isCellEditable={() => {
              return false;
            }}
            initialState={{
              columns: {
                columnVisibilityModel: columns.reduce(
                  (acc, curr) => ({ ...acc, [curr.field]: false }),
                  {},
                ),
              },
              sorting: {
                sortModel: [{ field: "lastModified", sort: "desc" }],
              },
            }}
            loading={!loaded || comparingIndices}
            onCellClick={handleCellClick}
            onRowSelectionModelChange={(newRowSelectionModel) =>
              setRowSelectionModel(newRowSelectionModel)
            }
            onSortModelChange={() => setIsSorted(true)}
            rows={rows}
            rowSelectionModel={rowSelectionModel}
            showToolbar
            slots={{
              toolbar: CustomToolbar,
            }}
            slotProps={{
              columnsManagement: {
                disableResetButton: true,
              },
            }}
            sx={{
              ".processing": {
                opacity: 0.5,
              },
              border: "none",
              display: "flex",
              "& .MuiDataGrid-iconButtonContainer": {
                marginLeft: "2px",
                visibility: "visible !important",
                width: "auto !important",
              },
              ".MuiDataGrid-row.Mui-selected": {
                backgroundColor: "rgba(25, 83, 183, 0.1)",
              },
              ".MuiDataGrid-row.MuiDataGrid-row--lastVisible": {
                borderBottom: "1px solid rgba(25, 83, 183, 0.1)",
              },
              "& .MuiDataGrid-filler > div:first-of-type": {
                borderTop: "none",
              },
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-row.Mui-selected:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
              [`.${gridClasses.row}.growRowAnimate`]: {
                animation: `${animationKeyFramesGrow} ${animationDuration} ${animationTiming}`,
              },
              [`.${gridClasses.row}.pulseRowAnimate`]: {
                animation: `${animationKeyFramesPulse} ${animationDuration} ${animationTiming}`,
              },
              [`.${gridClasses.row}.shrinkRowAnimate`]: {
                animation: `${animationKeyFramesShrink} ${animationDuration} ${animationTiming}`,
              },
            }}
          />
        </Stack>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          background: theme.palette.background.paper,
          p: 1,
          mx: 1,
          mb: 1,
        }}
      >
        <Box
          display="flex"
          justifyContent={isTrashView ? "flex-end" : "space-between"}
        >
          {isTrashView ? (
            <Button
              color="secondary"
              disabled={Boolean(numberTrashed === 0)}
              loading={!loaded || comparingIndices || isEmptying}
              onClick={() => {
                emptyTrash(af_case);
                removeTrashFilter();
              }}
              startIcon={
                Boolean(numberTrashed === 0) ? (
                  <DeleteForeverOutlined />
                ) : (
                  <DeleteForever />
                )
              }
              variant="outlined"
            >
              Empty Trash
            </Button>
          ) : (
            <>
              {/* additional level necessary to allow for canEditCase check */}
              {!isTrashView && (
                <>
                  {canEditCase(user, af_case) && (
                    <Tooltip
                      title={
                        isUploading ? "Uploading file, please wait" : "Add Clip"
                      }
                    >
                      <span>
                        <Button
                          aria-label="Add Clip"
                          loading={isUploading}
                          sx={{ mr: 1 }}
                          variant="contained"
                          startIcon={<Add />}
                          onClick={async () => {
                            await fullCaseReset(dispatch);
                            navigate(`/app/cases/${caseId}/addClip`);
                          }}
                        >
                          Import
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
              <Button
                color="secondary"
                disabled={numberTrashed === 0}
                loading={!loaded || comparingIndices || isEmptying}
                startIcon={numberTrashed > 0 ? <Delete /> : <DeleteOutlined />}
                variant={numberTrashed > 0 ? "contained" : "outlined"}
                onClick={applyTrashFilter}
              >
                Trash
                {numberTrashed > 0 && (
                  <Typography ml={0.75} variant="button">
                    {numberTrashed}
                  </Typography>
                )}
              </Button>
            </>
          )}
        </Box>
      </Paper>
      <ContentTitleBar
        caseFileNumber={af_case?.caseFileNumber}
        description={af_case?.description}
        fieldOffice={af_case?.fieldOffice}
        label={af_case?.caseName}
        isTrashView={isTrashView}
        removeTrashFilter={removeTrashFilter}
        menu={
          <CaseTitleBarMenu
            color="light"
            readOnly={readOnly}
            af_case={af_case}
            onChange={caseDataChanged}
          />
        }
        onBack={() => navigate("/app/cases")}
      />
      <Feature name={STIPA_STOI}>
        <StipaAlert />
      </Feature>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer,
        }}
      >
        <HistoryDrawer
          caseId={caseId}
          clipId={[...rowSelectionModel.ids][0] || null}
          page="clips"
        />
      </Box>

      {/* <CombineClipsDialog
        caseId={caseId}
        alignedClips={alignedClips}
        selectedAlignedClip={selectedAlignedClip}
      />
      <CombineClipsSnackbar /> */}
      <StoiFailureSnackbar />
    </Stack>
  );
}
